import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import AdSlot from "@/components/AdSlot";
import { format, startOfWeek, startOfMonth, parseISO } from "date-fns";
import { Trash, Plus, Wallet } from "@phosphor-icons/react";

const STORAGE_KEY = "tcl_budget_v1";

const CATEGORIES = [
  { slug: "food", name: "Food & Groceries", color: "#0d5f4b" },
  { slug: "transport", name: "Transport", color: "#c46a1c" },
  { slug: "housing", name: "Housing & Utilities", color: "#1e3a5f" },
  { slug: "entertainment", name: "Entertainment", color: "#5c3a0f" },
  { slug: "shopping", name: "Shopping", color: "#3a1e3f" },
  { slug: "health", name: "Health & Fitness", color: "#3d4d2a" },
  { slug: "bills", name: "Bills & Subscriptions", color: "#8a2b2b" },
  { slug: "other", name: "Other", color: "#4a4a4a" },
];

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "CAD", "AUD"];

function loadExpenses() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { expenses: [], budget: 2000, currency: "USD" };
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[Budget] Failed to parse saved data, resetting:", e);
    }
    return { expenses: [], budget: 2000, currency: "USD" };
  }
}

function saveExpenses(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function fmtCur(n, cur = "USD") {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: cur,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

export default function BudgetToolPage() {
  const [state, setState] = useState({ expenses: [], budget: 2000, currency: "USD" });
  const [form, setForm] = useState({
    amount: "",
    category: "food",
    note: "",
    date: format(new Date(), "yyyy-MM-dd"),
  });

  useEffect(() => {
    setState(loadExpenses());
  }, []);

  useEffect(() => {
    saveExpenses(state);
  }, [state]);

  const addExpense = (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const exp = {
      id: crypto.randomUUID?.() || String(Date.now()),
      amount,
      category: form.category,
      note: form.note.trim(),
      date: form.date,
      created_at: new Date().toISOString(),
    };
    setState((s) => ({ ...s, expenses: [exp, ...s.expenses] }));
    setForm({ ...form, amount: "", note: "" });
    toast.success("Expense added");
  };

  const removeExpense = (id) => {
    setState((s) => ({ ...s, expenses: s.expenses.filter((e) => e.id !== id) }));
  };

  const clearAll = () => {
    if (window.confirm("Clear all expenses? This cannot be undone.")) {
      setState((s) => ({ ...s, expenses: [] }));
      toast.success("All expenses cleared");
    }
  };

  const totals = useMemo(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    const weekStart = startOfWeek(new Date());
    const monthStart = startOfMonth(new Date());
    let dayT = 0, weekT = 0, monthT = 0, allT = 0;
    const byCat = {};
    for (const e of state.expenses) {
      allT += e.amount;
      byCat[e.category] = (byCat[e.category] || 0) + e.amount;
      if (e.date === today) dayT += e.amount;
      try {
        const d = parseISO(e.date);
        if (d >= weekStart) weekT += e.amount;
        if (d >= monthStart) monthT += e.amount;
      } catch (err) {
        // Invalid date string on this expense — skip week/month bucket but
        // keep the all-time total intact. Log in dev only.
        if (process.env.NODE_ENV !== "production") {
          console.warn("[Budget] Skipping expense with invalid date:", e.date, err);
        }
      }
    }
    return { dayT, weekT, monthT, allT, byCat };
  }, [state.expenses]);

  const monthPct = state.budget > 0 ? Math.min(100, (totals.monthT / state.budget) * 100) : 0;

  const cur = state.currency;
  const catMap = Object.fromEntries(CATEGORIES.map((c) => [c.slug, c]));

  return (
    <>
      <SEO
        title="Daily Budget Tracker"
        description="Free, private daily budget tracker. Log expenses, tag categories, see day/week/month totals. Data stays in your browser."
        url="/tools/budget"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Budget" }]} />
      </div>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b-2 border-foreground pb-8 flex items-start justify-between gap-6">
        <div>
          <div className="overline text-primary inline-flex items-center gap-2">
            <Wallet size={12} weight="bold" /> Tool 01
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mt-2">
            Daily Budget Tracker
          </h1>
          <p className="mt-3 max-w-2xl text-foreground/70">
            Log expenses in seconds. Track today, this week, and this month against your target. All data is stored locally in your browser.
          </p>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 lg:grid-cols-12">
        {/* Left: Form + totals */}
        <div className="lg:col-span-4 space-y-8">
          {/* Add expense */}
          <div className="border border-foreground/15 p-6">
            <div className="overline mb-4">Add Expense</div>
            <form onSubmit={addExpense} data-testid="budget-form" className="space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-foreground/60 block mb-2">Amount ({cur})</label>
                <input
                  data-testid="budget-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full h-12 px-3 border border-foreground/25 focus:border-foreground bg-transparent outline-none font-mono text-lg"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-foreground/60 block mb-2">Category</label>
                <select
                  data-testid="budget-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-11 px-3 border border-foreground/25 focus:border-foreground bg-transparent outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-foreground/60 block mb-2">Date</label>
                <input
                  data-testid="budget-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full h-11 px-3 border border-foreground/25 focus:border-foreground bg-transparent outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-foreground/60 block mb-2">Note (optional)</label>
                <input
                  data-testid="budget-note"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Coffee, Uber, etc."
                  className="w-full h-11 px-3 border border-foreground/25 focus:border-foreground bg-transparent outline-none"
                />
              </div>
              <button
                data-testid="budget-add-btn"
                type="submit"
                className="w-full h-12 bg-foreground text-background font-semibold hover:bg-primary transition-colors duration-200 inline-flex items-center justify-center gap-2"
              >
                <Plus size={16} weight="bold" /> Add Expense
              </button>
            </form>
          </div>

          {/* Settings */}
          <div className="border border-foreground/15 p-6">
            <div className="overline mb-4">Settings</div>
            <div className="grid gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-foreground/60 block mb-2">Monthly Budget</label>
                <input
                  data-testid="budget-target"
                  type="number"
                  min="0"
                  step="10"
                  value={state.budget}
                  onChange={(e) => setState({ ...state, budget: parseFloat(e.target.value) || 0 })}
                  className="w-full h-11 px-3 border border-foreground/25 focus:border-foreground bg-transparent outline-none font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-foreground/60 block mb-2">Currency</label>
                <select
                  data-testid="budget-currency"
                  value={state.currency}
                  onChange={(e) => setState({ ...state, currency: e.target.value })}
                  className="w-full h-11 px-3 border border-foreground/25 focus:border-foreground bg-transparent outline-none"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <button
                onClick={clearAll}
                data-testid="budget-clear"
                className="text-xs font-mono uppercase tracking-wider text-destructive hover:underline text-left"
              >
                Clear all expenses
              </button>
            </div>
          </div>
        </div>

        {/* Right: totals + list */}
        <div className="lg:col-span-8 space-y-8">
          {/* Totals */}
          <div className="grid gap-0 sm:grid-cols-4 border border-foreground/15">
            <div className="p-6 sm:border-r border-foreground/15" data-testid="total-day">
              <div className="overline">Today</div>
              <div className="font-display text-3xl font-black mt-2">{fmtCur(totals.dayT, cur)}</div>
            </div>
            <div className="p-6 sm:border-r border-foreground/15" data-testid="total-week">
              <div className="overline">This Week</div>
              <div className="font-display text-3xl font-black mt-2">{fmtCur(totals.weekT, cur)}</div>
            </div>
            <div className="p-6 sm:border-r border-foreground/15" data-testid="total-month">
              <div className="overline">This Month</div>
              <div className="font-display text-3xl font-black mt-2">{fmtCur(totals.monthT, cur)}</div>
            </div>
            <div className="p-6" data-testid="total-all">
              <div className="overline">All-Time</div>
              <div className="font-display text-3xl font-black mt-2">{fmtCur(totals.allT, cur)}</div>
            </div>
          </div>

          {/* Budget progress */}
          <div className="border border-foreground/15 p-6">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="overline">Monthly Budget Progress</div>
                <div className="mt-1 text-sm text-foreground/70 font-mono">
                  {fmtCur(totals.monthT, cur)} of {fmtCur(state.budget, cur)}
                </div>
              </div>
              <div className={`font-display font-black text-2xl ${monthPct > 90 ? "text-destructive" : "text-primary"}`}>
                {monthPct.toFixed(0)}%
              </div>
            </div>
            <div className="h-3 bg-muted border border-foreground/10 overflow-hidden">
              <div
                data-testid="budget-progress-bar"
                className={`h-full transition-all duration-300 ${monthPct > 90 ? "bg-destructive" : "bg-primary"}`}
                style={{ width: `${monthPct}%` }}
              />
            </div>
          </div>

          {/* Category breakdown */}
          {Object.keys(totals.byCat).length > 0 && (
            <div className="border border-foreground/15 p-6">
              <div className="overline mb-4">Spend by Category (all-time)</div>
              <div className="space-y-3">
                {Object.entries(totals.byCat)
                  .sort((a, b) => b[1] - a[1])
                  .map(([slug, amount]) => {
                    const cat = catMap[slug];
                    const pct = totals.allT > 0 ? (amount / totals.allT) * 100 : 0;
                    return (
                      <div key={slug} data-testid={`cat-row-${slug}`}>
                        <div className="flex items-baseline justify-between text-sm mb-1">
                          <span className="font-medium">{cat?.name || slug}</span>
                          <span className="font-mono text-foreground/70">{fmtCur(amount, cur)} · {pct.toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-muted overflow-hidden">
                          <div className="h-full" style={{ width: `${pct}%`, background: cat?.color || "#333" }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          <AdSlot size="leaderboard" />

          {/* Expense list */}
          <div>
            <div className="flex items-baseline justify-between border-b-2 border-foreground pb-3 mb-4">
              <h2 className="font-display text-2xl font-black">Recent expenses</h2>
              <span className="font-mono text-xs text-foreground/50">{state.expenses.length} entries</span>
            </div>
            {state.expenses.length === 0 ? (
              <div className="py-16 text-center text-foreground/60">
                No expenses yet. Add your first one on the left.
              </div>
            ) : (
              <div className="border border-foreground/15">
                {state.expenses.slice(0, 50).map((e) => {
                  const cat = catMap[e.category];
                  return (
                    <div
                      key={e.id}
                      data-testid={`expense-row-${e.id}`}
                      className="flex items-center gap-4 px-4 py-3 border-b border-foreground/10 last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
                    >
                      <div className="w-2 h-8 flex-shrink-0" style={{ background: cat?.color || "#333" }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="font-medium">{cat?.name || e.category}</span>
                          {e.note && <span className="text-sm text-foreground/60 truncate">· {e.note}</span>}
                        </div>
                        <div className="text-xs font-mono text-foreground/50 mt-0.5">{e.date}</div>
                      </div>
                      <div className="font-mono font-semibold">{fmtCur(e.amount, cur)}</div>
                      <button
                        onClick={() => removeExpense(e.id)}
                        data-testid={`delete-${e.id}`}
                        aria-label="Delete expense"
                        className="p-2 text-foreground/50 hover:text-destructive transition-colors duration-200"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
